import { useState, useEffect } from 'react';
import { leaveAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ConfirmationDialog, useConfirmation } from '../../components/ui/confirmation-dialog';
import { Loader2, Check, X, Users } from 'lucide-react';

const statusColors = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  APPROVED_BY_MANAGER: 'bg-blue-100 text-blue-800',
  APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-800',
};

const TeamLeaves = () => {
  const { dialogProps, confirm } = useConfirmation();
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPending();
  }, []);

  const fetchPending = async () => {
    try {
      const res = await leaveAPI.getPendingRequests();
      setPendingRequests(res.data);
    } catch (error) {
      toast.error('Failed to load team leave requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await leaveAPI.approveRequest(requestId);
      toast.success('Leave request approved');
      fetchPending();
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleReject = async (requestId) => {
    const result = await confirm({
      title: 'Reject Leave Request',
      description: 'Are you sure you want to reject this leave request? You can optionally provide a reason.',
      confirmText: 'Reject',
      variant: 'destructive',
      hasPrompt: true,
      promptLabel: 'Rejection Reason (optional)',
      promptPlaceholder: 'Enter reason for rejection...',
    });
    if (!result.confirmed) return;

    try {
      await leaveAPI.rejectRequest(requestId, result.value);
      toast.success('Leave request rejected');
      fetchPending();
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Team Leave Approvals</h1>
        <p className="text-muted-foreground text-sm sm:text-base">Review and approve leave requests from your team</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Pending Requests
            {pendingRequests.length > 0 && (
              <Badge variant="destructive">{pendingRequests.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {pendingRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
              <Users className="h-10 w-10 opacity-30" />
              <p className="text-sm">No pending leave requests from your team</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Leave Type</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Applied On</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell className="font-medium">{request.employee_name}</TableCell>
                    <TableCell>{request.leave_type_name}</TableCell>
                    <TableCell>{new Date(request.start_date).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(request.end_date).toLocaleDateString()}</TableCell>
                    <TableCell>{request.days_count}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{request.reason || '-'}</TableCell>
                    <TableCell>{new Date(request.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700 h-8 font-semibold shadow-sm"
                          onClick={() => handleApprove(request.id)}
                        >
                          <Check className="h-4 w-4 mr-1.5" /> Approve
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 h-8 font-semibold shadow-sm"
                          onClick={() => handleReject(request.id)}
                        >
                          <X className="h-4 w-4 mr-1.5" /> Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ConfirmationDialog {...dialogProps} />
    </div>
  );
};

export default TeamLeaves;
